import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { VideoCaptions, CaptionsButton, useCaptionsToggle } from "@/components/VideoCaptions";
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  ClipboardCheck,
  FileSignature,
  Pill,
  Brain,
  Heart,
  Phone,
  Apple,
  Play,
  CheckCircle2,
  Activity,
  Volume2,
  VolumeX,
} from "lucide-react";
import useScrollReveal from "@/hooks/useScrollReveal";

const APP_STORE_URL = "https://apps.apple.com/us/app/generational-health/id6751965153";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.generationalh.mobile.app&utm_source=na_Med";
import { StoreQRCodes } from "@/components/StoreQRCodes";

const HERO_IMG = "https://images.unsplash.com/photo-1670272506160-bdf0c7a45d2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwc3VwcG9ydCUyMGdyb3VwJTIwd2FybXxlbnwwfHx8fDE3Nzc5MTM3Mzd8MA&ixlib=rb-4.1.0&q=85";
const THERAPY_IMG = "https://images.pexels.com/photos/3958414/pexels-photo-3958414.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const GROUP_IMG = "https://images.unsplash.com/photo-1641057324798-c05769de628a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwc3VwcG9ydCUyMGdyb3VwJTIwd2FybXxlbnwwfHx8fDE3Nzc5MTM3Mzd8MA&ixlib=rb-4.1.0&q=85";

function Reveal({ children, className = "", as: As = "div", stagger = false }) {
  const [ref, visible] = useScrollReveal();
  return (
    <As
      ref={ref}
      className={`${stagger ? "reveal-stagger" : "reveal"} ${visible ? "is-visible" : ""} ${className}`}
    >
      {children}
    </As>
  );
}

function TopVideo() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [captionsOn, setCaptionsOn] = useCaptionsToggle();

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    if (!next) {
      // ensure it's playing after user interaction
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  };

  return (
    <section className="bg-navy relative" data-testid="home-top-video">
      <div className="w-full relative">
        <video
          ref={videoRef}
          src="https://customer-assets.emergentagent.com/job_gen-connect-11/artifacts/ckks3svq_GH_16x9_RM%20%281%29.mp4"
          className="w-full h-auto block"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="home-hero-video"
        />
        <VideoCaptions videoRef={videoRef} src="/captions/home-video.json" on={captionsOn} />
        <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 flex items-center gap-2">
        <CaptionsButton on={captionsOn} onToggle={() => setCaptionsOn((v) => !v)} />
        <button
          type="button"
          onClick={toggleSound}
          aria-label={muted ? "Unmute video" : "Mute video"}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-navy/70 backdrop-blur-md border border-white/20 text-white text-sm font-bold hover:bg-navy/90 transition-colors shadow-lg"
          data-testid="home-video-sound-toggle"
        >
          {muted ? (
            <>
              <VolumeX className="w-4 h-4" />
              <span className="hidden sm:inline">Tap for sound</span>
              <span className="sm:hidden">Sound</span>
            </>
          ) : (
            <>
              <Volume2 className="w-4 h-4" />
              <span className="hidden sm:inline">Mute</span>
            </>
          )}
        </button>
        </div>
      </div>
    </section>
  );
}

const PRESS_LOGOS = [
  { name: "Charlotte Business Journal" },
  { name: "Wells Fargo" },
  { name: "U.S. Department of Energy" },
  { name: "NC SBA" },
  { name: "South State Bank" },
];

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-white">
      {/* TOP VIDEO */}
      <TopVideo />

      {/* HERO — cinematic split */}
      <section className="relative overflow-hidden bg-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-20 pb-16 lg:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-light border border-teal/20 mb-7">
                <span className="w-2 h-2 rounded-full bg-teal pulse-dot" />
                <span className="text-xs font-bold tracking-[0.15em] uppercase text-teal-dark">Now in Early Access</span>
              </div>
              <h1 className="font-display text-[3rem] sm:text-[4.5rem] lg:text-[5.5rem] xl:text-[6.5rem] text-navy leading-[0.95] tracking-tight">
                It's <span className="hl-teal">OK</span>{" "}
                <br />
                not to be{" "}
                <span className="hl-teal">OK.</span>
              </h1>
              <p className="mt-7 text-lg sm:text-xl text-muted-ink max-w-xl leading-relaxed">
                A HIPAA-compliant mobile app that puts real-time, trauma-informed mental health
                care in your pocket — with an AI companion, licensed providers, and instant insurance verification.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link to="/download" className="btn-primary !px-7 text-base" data-testid="hero-download-button">
                  Get the app — free <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/features" className="btn-outline" data-testid="hero-features-button">
                  See how it works
                </Link>
              </div>
              <div className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-muted-ink font-medium">
                <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-teal" /> HIPAA compliant</span>
                <span className="flex items-center gap-2"><Brain className="w-4 h-4 text-teal" /> AI companion</span>
                <span className="flex items-center gap-2"><Phone className="w-4 h-4 text-teal" /> 24/7 support</span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-teal/10 rounded-[3rem] blur-3xl" aria-hidden="true" />
                <div className="relative hero-photo rounded-[2rem] overflow-hidden aspect-[4/5]">
                  <img
                    src={HERO_IMG}
                    alt="A diverse community connecting in warm light"
                    className="w-full h-full object-cover"
                    data-testid="hero-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRESS BAND — top 1% trust signal */}
      <section className="py-10 lg:py-12 bg-cream border-y border-slate-200" data-testid="home-press-band">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="text-center eyebrow !text-muted-ink mb-6">As featured in &amp; recognized by</p>
            <div className="flex flex-wrap items-center justify-center gap-x-10 lg:gap-x-16 gap-y-5">
              {PRESS_LOGOS.map((p, i) => (
                <span
                  key={i}
                  className="font-display text-lg sm:text-xl text-navy/70 hover:text-navy transition-colors"
                  data-testid={`home-press-${i}`}
                >
                  {p.name}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* MISSION BAND */}
      <section className="py-20 lg:py-28" data-testid="mission-band">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <p className="eyebrow mb-4">Our mission</p>
            <p className="font-display text-3xl sm:text-4xl lg:text-5xl text-navy leading-[1.15]">
              To break <span className="hl-teal">generational cycles of trauma</span> and create pathways
              to healing, stability, and long-term wellness — especially for those historically underserved.
            </p>
          </Reveal>
        </div>
      </section>

      {/* BY THE NUMBERS */}
      <section className="py-16 lg:py-20 bg-navy text-white relative overflow-hidden" data-testid="stats-section">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] bg-teal/15 rounded-full blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="text-center eyebrow !text-teal mb-3">By the numbers</p>
            <h2 className="text-center font-display text-3xl sm:text-4xl lg:text-5xl text-white mb-14 leading-[1.1]">
              A platform built on <span className="hl-teal">measurable trust.</span>
            </h2>
          </Reveal>
          <Reveal stagger className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {[
              { n: "< 2", u: "min", l: "From download to support" },
              { n: "24/7", u: "", l: "Crisis line never sleeps" },
              { n: "5", u: "+", l: "Integrated clinical systems*" },
              { n: "100", u: "%", l: "HIPAA &amp; integrity-monitored" },
            ].map((s, i) => (
              <div key={i} className="text-left" data-testid={`stat-${i}`}>
                <p className="font-display text-6xl lg:text-7xl text-teal leading-none tabular">
                  {s.n}<span className="text-4xl">{s.u}</span>
                </p>
                <p className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-white/70" dangerouslySetInnerHTML={{ __html: s.l }} />
              </div>
            ))}
          </Reveal>
          <p className="text-xs text-white/50 mt-10 text-center lg:text-left">*pVerify · DoseSpot · Drug Bank · DocuSign · Real-time integrity monitoring</p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24 lg:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <p className="eyebrow mb-4">The whole stack</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
                Every tool a modern mental health journey needs — <span className="hl-teal">in one app.</span>
              </h2>
            </div>
          </Reveal>
          <Reveal stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: <Brain className="w-6 h-6" />, title: "AI Genius Companion", desc: "A genius-level AI that listens, teaches coping skills, and seamlessly brings in your care team when it matters." },
              { icon: <ClipboardCheck className="w-6 h-6" />, title: "Insurance Verified Instantly", desc: "pVerify auto-verifies Medicaid, Medicare, and commercial plans in seconds — no paper, no waiting." },
              { icon: <Pill className="w-6 h-6" />, title: "E-Prescribing", desc: "Built-in DoseSpot and Drug Bank so providers prescribe, track, and counsel with clinical safety." },
              { icon: <FileSignature className="w-6 h-6" />, title: "Consent for Minors & Emancipated Youth", desc: "DocuSign flows built for families and youth — legally compliant, clear, and protective." },
              { icon: <Stethoscope className="w-6 h-6" />, title: "Patented Provider Backflow", desc: "Our patented workflow reroutes patient signals to the right provider at the right moment — clinically, quietly, fast." },
              { icon: <Phone className="w-6 h-6" />, title: "24/7 Crisis Support", desc: "Crisis specialists, peer supporters, and licensed professionals — matched to your moment, any hour." },
              { icon: <Activity className="w-6 h-6" />, title: "Real-Time Integrity Monitoring", desc: "A built-in metric system tracks patient and provider activity to eliminate fraud — protecting clinicians, payers, and patients." },
            ].map((f, i) => (
              <div key={i} className="gh-card" data-testid={`feature-card-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-teal-light text-teal flex items-center justify-center mb-5">
                  {f.icon}
                </div>
                <h3 className="font-display text-xl mb-2 text-navy">{f.title}</h3>
                <p className="text-muted-ink text-[15px] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </Reveal>
          <div className="mt-10">
            <Link to="/features" className="btn-outline-teal" data-testid="home-all-features-link">
              Explore all features <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* DOWNLOAD BAND — dedicated, deserves its own moment */}
      <section className="py-20 lg:py-28 bg-cream relative overflow-hidden" data-testid="download-band">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal>
              <p className="eyebrow mb-4">Download today</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
                Help is here. <span className="hl-teal">When you need it.</span>
              </h2>
              <p className="mt-7 text-lg text-muted-ink leading-relaxed max-w-lg">
                Free to download — no cost, no subscription. Setup in under two minutes. The AI Companion is ready the moment you open it —
                a real human is two minutes behind. Clinical services (therapists, peer support, group therapy) are billed to insurance or on a sliding scale.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="bg-navy hover:bg-navy-light text-white rounded-xl px-5 py-3.5 flex items-center gap-3 transition" data-testid="hero-app-store-link">
                  <Apple className="w-7 h-7" />
                  <div className="leading-tight text-left">
                    <p className="text-[10px] uppercase tracking-wider text-white/60">Download on</p>
                    <p className="text-base font-bold">App Store</p>
                  </div>
                </a>
                <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="bg-navy hover:bg-navy-light text-white rounded-xl px-5 py-3.5 flex items-center gap-3 transition" data-testid="hero-play-store-link">
                  <Play className="w-7 h-7 fill-white" />
                  <div className="leading-tight text-left">
                    <p className="text-[10px] uppercase tracking-wider text-white/60">Get it on</p>
                    <p className="text-base font-bold">Google Play</p>
                  </div>
                </a>
              </div>
            </Reveal>

            <Reveal>
              <div className="relative max-w-md mx-auto lg:ml-auto">
                <div className="absolute -inset-6 bg-teal/15 rounded-[3rem] blur-3xl" aria-hidden="true" />
                <StoreQRCodes eyebrow="Or scan to download" footer="Point your phone camera at your store's code." />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WE BELIEVE */}
      <section className="py-24 lg:py-32" data-testid="we-believe-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="w-14 h-14 rounded-2xl bg-teal-light text-teal flex items-center justify-center mb-6">
              <Heart className="w-7 h-7" fill="currentColor" />
            </div>
            <p className="eyebrow mb-4">We believe</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
              Healing should not be a <span className="hl-teal">privilege.</span> It should be a <span className="hl-teal">right.</span>
            </h2>
          </Reveal>
          <Reveal stagger className="space-y-5">
            {[
              "Your story matters — every single version of it.",
              "Help should be immediate and accessible.",
              "Technology should remove barriers, not add them.",
              "Care should reach across generations.",
            ].map((b, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`believe-${i}`}>
                <span className="mt-0.5 w-6 h-6 rounded-full bg-teal flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-4 h-4 text-white" />
                </span>
                <p className="text-lg text-navy font-medium">{b}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* AUDIENCE CARDS */}
      <section className="py-20 lg:py-28 bg-cream" data-testid="audience-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal stagger className="grid md:grid-cols-2 gap-6">
            <Link to="/features" className="gh-card group !p-10 flex flex-col justify-between min-h-[340px]" data-testid="audience-patients">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-light text-teal text-xs font-bold uppercase tracking-widest mb-6">
                  <Sparkles className="w-3.5 h-3.5" /> For Patients
                </div>
                <h3 className="font-display text-3xl sm:text-4xl text-navy mb-4 leading-tight">A therapist, a coach, and a friend — always within reach.</h3>
                <p className="text-muted-ink leading-relaxed">
                  Chat with our AI companion any hour. Book licensed providers. Verify your insurance. Get prescriptions
                  managed safely. All inside one app built for your privacy.
                </p>
              </div>
              <span className="mt-8 text-teal font-bold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Explore patient features <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link to="/for-providers" className="gh-card-dark group !p-10 flex flex-col justify-between min-h-[340px]" data-testid="audience-providers">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-teal text-xs font-bold uppercase tracking-widest mb-6">
                  <Stethoscope className="w-3.5 h-3.5" /> For Providers
                </div>
                <h3 className="font-display text-3xl sm:text-4xl mb-4 text-white leading-tight">A patented workflow built around how you actually practice.</h3>
                <p className="text-white/75 leading-relaxed">
                  Our patented backflow routes the right signal at the right moment. Paired with DoseSpot, Drug Bank,
                  DocuSign, pVerify, and real-time integrity monitoring — so clinical work stays clinical.
                </p>
              </div>
              <span className="mt-8 text-teal font-bold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                For clinicians <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* UPCOMING EVENT TEASER */}
      <section className="py-20 lg:py-28 bg-cream relative overflow-hidden" data-testid="home-event-teaser">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <Reveal className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-teal/15 rounded-[2rem] blur-3xl" aria-hidden="true" />
                <div className="relative rounded-[2rem] overflow-hidden bg-white border border-slate-200 shadow-xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_gen-connect-11/artifacts/fgxw0zum_unfiltered%20minds%20event.jpg"
                    alt="Unfiltered Minds — A nationwide mental health open mic"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </Reveal>
            <Reveal className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal text-white text-xs font-bold uppercase tracking-[0.15em] mb-6">
                <span className="w-2 h-2 rounded-full bg-white pulse-dot" />
                Upcoming · May 5, 2026
              </div>
              <p className="eyebrow mb-3">An evening with Generational Health</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
                <span className="hl-teal">Unfiltered Minds.</span><br />
                A nationwide mental health open mic.
              </h2>
              <p className="mt-6 text-lg text-muted-ink leading-relaxed max-w-xl">
                Come share. Come listen. Come change the conversation. An honest, beautiful night of community
                in Charlotte — DJ, live music, charcuterie, and the kind of room where the stigma loses its grip.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/events" className="btn-primary" data-testid="home-event-details">
                  Event details <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="https://maps.google.com/?q=615+E+Morehead+St+Floor+24+Charlotte+NC+28202"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline"
                  data-testid="home-event-directions"
                >
                  Get directions
                </a>
              </div>
              <p className="mt-7 text-xs text-muted-ink uppercase tracking-[0.2em] font-bold">
                Co-hosted by <span className="text-navy">Madina PLLC</span> · <span className="text-navy">Generational Health</span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DONATE */}
      <section className="py-24 bg-navy text-white relative overflow-hidden" data-testid="donate-cta-section">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-teal/20 rounded-full blur-3xl" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <p className="eyebrow !text-teal mb-4">Fund the mission</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6 text-white">
              $25 puts this app in the hands of <span className="hl-teal">someone who can't afford care.</span>
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-9">
              Your donation sponsors app access, insurance navigation, and provider hours for underserved communities —
              breaking generational cycles, one life at a time.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/donate" className="btn-primary" data-testid="donate-cta-button">
                Donate now <Heart className="w-5 h-5" />
              </Link>
              <Link to="/partner" className="btn-outline-teal" data-testid="partner-cta-button">
                Partner with us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-24 lg:py-32" data-testid="quote-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <p className="eyebrow mb-6">From the community</p>
            <blockquote className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-navy">
              "I downloaded the app at 2 a.m. on the worst night of my life. The AI grounded me — and a real person was
              on the line within a minute. <span className="hl-teal">That's the reason I'm still here.</span>"
            </blockquote>
            <p className="mt-8 text-muted-ink font-medium">— Anonymous, shared with permission</p>
            <Link to="/stories" className="mt-8 inline-flex items-center gap-2 text-teal font-bold link-underline" data-testid="home-stories-link">
              Read more stories <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
